/**
 * MessageComposer — composer simples da ThreadView da mensageria interna
 * (Item #8b, Slice D).
 *
 * Co-located e DELIBERADAMENTE enxuto: textarea auto-crescente + botão Enviar.
 * NÃO reusa o Composer da Conversa-em-curso (anexos, adversarial, templates,
 * janela 24h) — aqui é operador↔operador, "casa", sem essa maquinaria (v1 sem
 * anexos).
 *
 * Convenção de teclado igual à Conversa-em-curso: Enter envia, Shift+Enter
 * quebra linha. Vazio/só-espaços não envia. O envio (e a invalidação do thread/
 * não-lido/lista) fica no `onSend` do pai via `useReply`.
 */

import { Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

const MAX_HEIGHT_PX = 120;

interface MessageComposerProps {
  /** Envia o corpo já validado (não-vazio, trimado). */
  onSend: (body: string) => void;
  /** Desabilita o envio enquanto a mutação está em curso. */
  isSending: boolean;
}

export function MessageComposer({ onSend, isSending }: MessageComposerProps): JSX.Element {
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !isSending;

  // Auto-crescer a textarea conforme o conteúdo (até MAX_HEIGHT_PX).
  useEffect(() => {
    const el = textareaRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX).toString()}px`;
  }, [draft]);

  const submit = useCallback(() => {
    const body = draft.trim();
    if (body.length === 0 || isSending) return;
    onSend(body);
    setDraft('');
  }, [draft, isSending, onSend]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter envia; Shift+Enter quebra linha (deixa o comportamento padrão).
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  return (
    <div className="dm-composer">
      <div className="dm-composer__box">
        <textarea
          ref={textareaRef}
          className="dm-composer__input"
          value={draft}
          rows={1}
          placeholder="Escreva uma mensagem interna…"
          aria-label="Escreva uma mensagem interna"
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="dm-composer__send"
          aria-label="Enviar"
          title="Enviar (Enter)"
          disabled={!canSend}
          onClick={submit}
        >
          <Send size={15} strokeWidth={1.9} aria-hidden />
        </button>
      </div>
      <span className="dm-composer__hint">Enter envia · Shift+Enter quebra linha</span>
    </div>
  );
}
